<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    :submitTitle="submitTitle"
    icon="fa-money-check-edit-alt"
    :scrollable="true"
    :showSubmit="editing"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="dialogForm" :labelColumnSize="2">
      <FormRow label="Type">
        <FormSelect v-if="editing" :options="updateTypeLookup" v-model="itemData.priceType" field="priceType" />
        <FormStaticLabel v-if="!editing">{{itemData.priceType | formatLookupName(updateTypeLookup)}}</FormStaticLabel>
      </FormRow>
      <FormHorizontalRule />
      <h4>Filter</h4>
      <FormUpdateFilter label="Customer Filter" v-show="isCustomer" :options="customerLookup" v-model="itemData.customerFilter" field="customerFilter" :editing="editing" />
      <FormUpdateFilter label="Customer Label Filter" v-show="isCustomer" :options="customerLabelLookup" v-model="itemData.customerLabelFilter" field="customerLabelFilter" :editing="editing" />
      <FormUpdateFilter label="Stock Filter" :options="stockLookup" v-model="itemData.stockFilter" field="stockFilter" :editing="editing" />
      <FormUpdateFilter label="Stock Type Filter" :options="stockTypeLookup" v-model="itemData.stockTypeFilter" field="stockTypeFilter" :editing="editing" />
      <FormUpdateFilter label="Category Filter" :options="categoryLookup" v-model="itemData.categoryFilter" field="categoryFilter" :editing="editing" />
      <FormUpdateFilter label="Price Group Filter" :options="priceGroupLookup" v-model="itemData.priceGroupFilter" field="priceGroupFilter" :editing="editing" />
      <FormUpdateFilter label="Service Range Filter" :options="serviceRangeLookup" v-model="itemData.serviceRangeFilter" field="serviceRangeFilter" :editing="editing" />

      <FormRow label="Options">
        <FormCheckbox v-if="editing" label="Ignore Zero Prices" v-model="itemData.ignoreZeroPrice" field="ignoreZeroPrice" />&nbsp;&nbsp;
        <FormCheckbox v-if="editing" label="Active Prices Only" v-model="itemData.activePricesOnly" field="activePricesOnly" />&nbsp;&nbsp;
        <FormCheckbox v-if="editing" label="Include Unchanged Prices" v-model="itemData.includeUnchangedPrices" field="includeUnchangedPrices" />
        <FormStaticLabel v-if="!editing">
          <IconBoolean :value="itemData.ignoreZeroPrice" /> Ignore Zero Prices<br />
          <IconBoolean :value="itemData.activePricesOnly" /> Active Prices Only<br />
          <IconBoolean :value="itemData.includeUnchangedPrices" /> Include Unchanged Prices<br />
        </FormStaticLabel>
      </FormRow>
      <FormHorizontalRule />

      <h4 class="mt-4">Adjustment</h4>
      <FormRow label="Adjustment">
        <div class="row">
          <div class="col-lg-3">
            <FormSelect v-if="editing" :options="adjustmentTypeLookup" v-model="itemData.adjustmentType" field="adjustmentType" />
            <FormStaticLabel v-if="!editing">{{itemData.adjustmentType | formatLookupName(adjustmentTypeLookup)}}</FormStaticLabel>
          </div>
          <div class="col-lg-9">
            <FormInputText addon="%" type="number" v-show="editing && isAdjustPercent" v-model="itemData.adjustmentValue" field="adjustmentValue" />
            <FormInputCurrency v-show="editing && isAdjustAmount" v-model="itemData.adjustmentValue" field="adjustmentValue" />

            <FormStaticLabel v-if="!editing && isAdjustPercent">{{itemData.adjustmentValue | formatPercent}}</FormStaticLabel>
            <FormStaticLabel v-if="!editing && isAdjustAmount">{{itemData.adjustmentValue | formatCurrency}}</FormStaticLabel>
          </div>
        </div>
      </FormRow>
      <FormRow label="Rounding">
        <div class="row">
          <div class="col-lg-3">
            <FormCheckbox v-if="editing" label="Apply Rounding to Nearest" v-model="itemData.applyRounding" field="applyRounding" />
            <FormStaticLabel v-if="!editing">
              {{itemData.applyRounding ? 'Apply Rounding to Nearest' : 'None'}}
            </FormStaticLabel>
          </div>
          <div class="col-lg-9">
            <FormInputCurrency v-show="editing && itemData.applyRounding" v-model="itemData.roundingValue" field="roundingValue" />
            <FormStaticLabel v-if="!editing && itemData.applyRounding">
              {{itemData.roundingValue | formatCurrency}}
            </FormStaticLabel>
          </div>
        </div>
      </FormRow>
      <small v-if="isCustomer">Note: Adjustments are only applied to fixed prices in customer price lists</small>
      
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import { mapState } from "vuex"

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import { PriceTypes, FilterOptionTypes, BulkUpdateAdjustmentTypes, InventoryStockTypes, InventoryStockTypeLookup, PricingSystemTypes } from '@/store/types'

export default {
  name: "PriceUpdateItemDialog",
  mixins: [
    ModalDialogMixin,
    form,
  ],
  components: {
    ModalDialog
  },
  props: {
    isCreateMode: Boolean,
    editing: Boolean,
    updateItem: Object,
    lookupDetails: Object
  },
  watch: {
    updateItem: function(newVal, oldVal) {
      this.itemData = _.cloneDeep(newVal)
    },
    lookupDetails: function(newVal, oldVal) {
      this.customerLookup = newVal.customerLookup
      this.customerLabelLookup = newVal.customerLabelLookup
      this.stockLookup = newVal.stockLookup
      this.categoryLookup = newVal.categoryLookup
      this.priceGroupLookup = newVal.priceGroupLookup
      this.serviceRangeLookup = newVal.serviceRangeLookup
    }
  },
  data() {
    return {
      dialogForm: {},
      itemData: {
        // Defaults
        priceType: PriceTypes.sales,

        customerFilter: {},
        customerLabelFilter: {},
        stockFilter: {},
        stockTypeFilter: {},
        categoryFilter: {},
        priceGroupFilter: {},
        serviceRangeFilter: {},

        ignoreZeroPrice: true,
        activePricesOnly: true,
        includeUnchangedPrices: false,
        adjustmentType: BulkUpdateAdjustmentTypes.increasePercent,
        adjustmentValue: 0,
        applyRounding: true,
        roundingValue: 0.05,
      },

      customerLookup: this.$props.lookupDetails.customerLookup,
      customerLabelLookup: this.$props.lookupDetails.customerLabelLookup,
      stockLookup: this.$props.lookupDetails.stockLookup,
      stockTypeLookup: InventoryStockTypeLookup.filter(type => type.value != InventoryStockTypes.template),   // Don't include template types
      categoryLookup: this.$props.lookupDetails.categoryLookup,
      priceGroupLookup: this.$props.lookupDetails.priceGroupLookup,
      serviceRangeLookup: this.$props.lookupDetails.serviceRangeLookup,

      adjustmentTypeLookup: [
        { value: BulkUpdateAdjustmentTypes.increasePercent, name: 'Increase Percentage' },
        { value: BulkUpdateAdjustmentTypes.decreasePercent, name: 'Decrease Percentage' },
        { value: BulkUpdateAdjustmentTypes.increaseAmount, name: 'Increase Amount' },
        { value: BulkUpdateAdjustmentTypes.decreaseAmount, name: 'Decrease Amount' }
      ],      
    }
  },
  mounted() {
    if(this.$props.updateItem) {
      this.$data.itemData = _.cloneDeep(this.$props.updateItem)
    }
  },
  computed: {
    ...mapState({
      accountPricingSystemType: state => state.account.pricingSystemType
    }),
    title() {
      if(!this.editing)
        return 'View Bulk Price Adjustment'
      return this.isCreateMode ? 'New Bulk Price Adjustment' : 'Edit Bulk Price Adjustment'
    },
    submitTitle() {
      return this.isCreateMode ? 'Add' : 'Update'
    },
    isCustomer() {
      return this.$data.itemData.priceType == PriceTypes.customer
    },
    isAdjustPercent() {
      return this.$data.itemData.adjustmentType == BulkUpdateAdjustmentTypes.increasePercent ||
      this.$data.itemData.adjustmentType == BulkUpdateAdjustmentTypes.decreasePercent
    },
    isAdjustAmount() {
      return this.$data.itemData.adjustmentType == BulkUpdateAdjustmentTypes.increaseAmount ||
      this.$data.itemData.adjustmentType == BulkUpdateAdjustmentTypes.decreaseAmount
    },
    updateTypeLookup() {
      var lookup = [{ value: PriceTypes.sales, name: 'Sales' }]
      if(this.accountPricingSystemType != PricingSystemTypes.simple)
        lookup.push({ value: PriceTypes.customer, name: 'Customer' })

      return lookup
    }
  },
  methods: {
    show() {
      
      this.dialogForm = this.clearFormResponse()
      this.submitting = false

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.dialogForm = this.clearFormResponse()

      // Run simple validation
      if(parseFloat(this.itemData.adjustmentValue) <= 0 || isNaN(parseFloat(this.itemData.adjustmentValue))) {
        this.dialogForm = this.addFormFieldError(this.dialogForm, 'adjustmentValue', 'Adjustment must be greater than zero')
        return
      }

      this.modalClosed(this.itemData)
      this.hide()
    },
  }
}
</script>